<template>
  <v-container fluid full-height grid-list-md>
    <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onSave">
      <v-layout row wrap>
        <v-flex xs6>
          <div class="text-xs-left">
            <v-breadcrumbs :items="breadcrumbs" divider=">">
              <template slot="item" slot-scope="props">
                <router-link
                  :to="'/' + props.item.to"
                  :disabled="props.item.disabled"
                  v-bind:class="{
                    'v-breadcrumbs__item v-breadcrumbs__item--disabled':
                      props.item.disabled,
                  }"
                  >{{ props.item.text }}</router-link
                >
              </template>
            </v-breadcrumbs>
          </div>
        </v-flex>
        <v-flex xs6>
          <div class="text-xs-right">
            <v-btn outline color="primary" @click="goBack">
              Go Back
              <v-icon right>arrow_back</v-icon>
            </v-btn>
          </div>
        </v-flex>
        <v-flex>
          <v-card>
            <v-layout row wrap class="pa-3">
              <v-flex sm12 v-if="responseError">
                <span class="red--text">* {{ responseError }}</span>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="code"
                  label="Code"
                  v-model="machine.code"
                  type="text"
                  :rules="rules.lengthRequiredRules(20, 'Code')"
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-select
                  :items="statusList"
                  label="Status"
                  v-model="machine.status"
                  item-text="label"
                  item-value="value"
                  :rules="rules.required('Status')"
                ></v-select>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="name"
                  label="Name"
                  v-model="machine.name"
                  type="text"
                  :rules="rules.lengthRequiredRules(100, 'Name')"
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-autocomplete
                  v-model="machine.shopId"
                  :items="shopList"
                  :hide-no-data="true"
                  color="blue-grey lighten-2"
                  label="Choose Shop..."
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  :rules="rules.required('Shop')"
                  :readonly="checkUserShop() ? true : false"
                ></v-autocomplete>
              </v-flex>
              <v-flex sm6>
                <v-autocomplete
                  v-model="machine.departmentId"
                  :items="departmentList"
                  :hide-no-data="true"
                  color="blue-grey lighten-2"
                  label="Choose Department..."
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  :rules="rules.required('Department')"
                ></v-autocomplete>
              </v-flex>
              <v-flex sm6>
                <v-autocomplete
                  v-model="machine.branchId"
                  :items="branchList"
                  :hide-no-data="true"
                  color="blue-grey lighten-2"
                  label="Choose Branch..."
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  :rules="rules.required('Branch')"
                  :readonly="checkUserBranch() ? true : false"
                ></v-autocomplete>
              </v-flex>
              <v-flex sm6>
                <v-autocomplete
                  v-model="machine.unitId"
                  :items="unitList"
                  :hide-no-data="true"
                  color="blue-grey lighten-2"
                  label="Choose Unit..."
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  :rules="rules.required('Unit')"
                ></v-autocomplete>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="qty"
                  label="Qty"
                  v-model="machine.qty"
                  :min="0"
                  type="number"
                  :rules="rules.qtyRules"
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="model"
                  label="Model"
                  v-model="machine.model"
                  type="text"
                  :rules="rules.lengthRequiredRules(300, 'Model')"
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="capacity"
                  label="Capacity"
                  v-model="machine.capacity"
                  type="text"
                  :rules="rules.lengthRequiredRules(100, 'Capacity')"
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="purchaseOrderAmount"
                  label="Purchase Amount"
                  v-model="machine.purchaseOrderAmount"
                  type="number"
                  @input="calculateCurrentAmount()"
                  :rules="rules.zeroAmountRules('Purchase amount')"
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="currentAmount"
                  label="Current Amount"
                  v-model="machine.currentAmount"
                  type="number"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-autocomplete
                  v-model="machine.countryId"
                  :items="countryList"
                  :hide-no-data="true"
                  color="blue-grey lighten-2"
                  label="Choose Country Of Origin..."
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  clearable
                ></v-autocomplete>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="installationData"
                  label="Installation Data"
                  v-model="machine.installationData"
                  type="text"
                  :rules="rules.lengthRequiredRules(100, 'Installation data')"
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-text-field
                  name="lifeCycle"
                  label="Life Cycle"
                  v-model="machine.lifeCycle"
                  type="text"
                  :rules="rules.lengthRules(300, 'Life cycle')"
                ></v-text-field>
              </v-flex>
              <v-flex sm6>
                <v-autocomplete
                  v-model="machine.maintenanceTypeId"
                  :items="maintenanceTypeList"
                  :hide-no-data="true"
                  color="blue-grey lighten-2"
                  label="Choose Maintenance Type..."
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  :rules="rules.required('Maintenance type')"
                ></v-autocomplete>
              </v-flex>
              <v-flex sm6>
                <v-autocomplete
                  v-model="machine.machineTypeId"
                  :items="machineTypeList"
                  :hide-no-data="true"
                  color="blue-grey lighten-2"
                  label="Choose Machine Type..."
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  :rules="rules.required('Machine type')"
                ></v-autocomplete>
              </v-flex>
              <v-flex sm6>
                <v-autocomplete
                  v-model="machine.maintenanceFrequencyId"
                  :items="maintenanceFrequencyList"
                  :hide-no-data="true"
                  color="blue-grey lighten-2"
                  label="Choose Maintenance Frequency..."
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  :rules="rules.required('Maintenance frequency')"
                ></v-autocomplete>
              </v-flex>
              <v-flex sm12>
                <v-text-field
                  name="remark"
                  label="Remark"
                  v-model="machine.remark"
                  type="text"
                  :rules="rules.lengthRules(500, 'Remark')"
                ></v-text-field>
              </v-flex>
              <v-flex sm12>
                <v-text-field
                  name="description"
                  label="Description"
                  v-model="machine.description"
                  type="text"
                  :rules="rules.lengthRules(500, 'Description')"
                ></v-text-field>
              </v-flex>
              <v-flex sm6 v-if="machine.id">
                <v-menu
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                  :disabled="true"
                >
                  <v-text-field
                    slot="activator"
                    label="Created Date"
                    type="text"
                    prepend-icon="event"
                    v-model="createdDate"
                    readonly
                  ></v-text-field>
                  <v-date-picker v-model="createdDate"></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex sm6 v-if="machine.id">
                <v-text-field
                  name="createdBy"
                  label="Created By"
                  v-model="machine.createdBy"
                  type="text"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex sm6 v-if="machine.id">
                <v-menu
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                  :disabled="true"
                >
                  <v-text-field
                    slot="activator"
                    label="Updated Date"
                    type="text"
                    prepend-icon="event"
                    v-model="updatedDate"
                    readonly
                  ></v-text-field>
                  <v-date-picker v-model="updatedDate"></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex sm6 v-if="machine.id">
                <v-text-field
                  name="updateBy"
                  label="Updated By"
                  v-model="machine.updatedBy"
                  type="text"
                  readonly
                ></v-text-field>
              </v-flex>

              <!--maintenance track record -->
              <v-flex sm12>
                <v-card>
                  <v-card-title>
                    <v-flex xs12>
                      <v-btn color="success" @click="addMachineDetail()">
                        ADD MAINTENANCE TRACK RECORD
                        <v-icon right>add</v-icon>
                      </v-btn>
                      <span>Total Maintenance Amount</span>
                      <v-chip>
                        <span>
                          {{ totalMaintenanceAmount | decFormat(2) }}
                        </span>
                      </v-chip>
                      <span>Total Frequencies</span>
                      <v-chip>
                        <span>
                          {{ totalFrequency | decFormat(2) }}
                        </span>
                      </v-chip>
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field
                        v-model="failureItemSearch"
                        placeholder="Enter failure item"
                        label="Search"
                        single-line
                        hide-details
                        @input="calculateFilterTotalAmount()"
                      ></v-text-field>
                    </v-flex>
                  </v-card-title>
                  <v-card-text>
                    <v-data-table
                      :headers="maintenanceRecordHeaders"
                      :items="machineDetailList"
                      class="elevation-1"
                    >
                      <template v-slot:items="props">
                        <td>
                          <v-text-field
                            v-model="props.item.failureMode"
                            type="text"
                            :rules="rules.lengthRules(300, 'Failure mode')"
                            style="width: 200px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-menu
                            :nudge-right="40"
                            lazy
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="290px"
                          >
                            <v-text-field
                              slot="activator"
                              v-model="props.item.date"
                              label="Date"
                              prepend-icon="event"
                              readonly
                              style="width: 150px"
                              clearable
                              @click:clear="props.item.date = ''"
                            ></v-text-field>
                            <v-date-picker
                              v-model="props.item.date"
                            ></v-date-picker>
                          </v-menu>
                        </td>
                        <td>
                          <v-text-field
                            v-model="props.item.failureItem"
                            type="text"
                            :rules="rules.lengthRules(300, 'Failure item')"
                            style="width: 200px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-text-field
                            v-model="props.item.correctiveAction"
                            type="text"
                            :rules="rules.lengthRules(300, 'Corrective action')"
                            style="width: 200px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-text-field
                            v-model="props.item.maintenanceAmount"
                            type="number"
                            @input="
                            calculateCurrentAmount(),
                                calculateFilterTotalAmount()
                            "
                            :rules="rules.zeroAmountRules('Maintenance amount')"
                            style="width: 220px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-text-field
                            v-model="props.item.frequency"
                            type="number"
                            @input="calculateFilterTotalAmount()"
                            :rules="rules.zeroAmountRules('Frequency')"
                            style="width: 200px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-btn
                            icon
                            @click="removeMachineDetail(props.item.id)"
                          >
                            <v-icon>cancel</v-icon>
                          </v-btn>
                        </td>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-flex>

              <!--machine check list -->
              <v-flex sm12>
                <v-card>
                  <v-card-title>
                    <v-btn color="success" @click="addMachineChecklistDetail()">
                      ADD MACHINE CHECKLIST
                      <v-icon right>add</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <v-data-table
                      :headers="machineChecklistHeaders"
                      :items="machine.machineChecklistDetailList"
                      class="elevation-1"
                    >
                      <template v-slot:items="props">
                        <td>
                          <v-text-field
                            v-model="props.item.location"
                            type="text"
                            :rules="rules.lengthRequiredRules(100, 'Location')"
                            style="width: 200px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-text-field
                            v-model="props.item.workDescription"
                            type="text"
                            :rules="rules.lengthRules(500, 'Work description')"
                            style="width: 200px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-autocomplete
                            v-model="props.item.maintenanceFrequencyIds"
                            :items="maintenanceFrequencyList"
                            :hide-no-data="true"
                            color="blue-grey lighten-2"
                            label="Select"
                            item-text="name"
                            item-value="id"
                            menu-props="auto"
                            multiple
                            chips
                            style="width: 200px"
                          ></v-autocomplete>
                        </td>
                        <td>
                          <v-text-field
                            v-model="props.item.maintenanceRemark"
                            type="text"
                            :rules="
                              rules.lengthRules(500, 'Maintenance remark')
                            "
                            style="width: 200px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-autocomplete
                            v-model="props.item.checklistFrequencyIds"
                            :items="checklistFrequencyList"
                            :hide-no-data="true"
                            color="blue-grey lighten-2"
                            label="Select"
                            item-text="name"
                            item-value="id"
                            menu-props="auto"
                            multiple
                            chips
                            style="width: 200px"
                          ></v-autocomplete>
                        </td>
                        <td>
                          <v-text-field
                            v-model="props.item.checklistRemark"
                            type="text"
                            :rules="rules.lengthRules(500, 'Check List Remark')"
                            style="width: 200px"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-btn
                            icon
                            @click="removeMachineChecklistDetail(props.item.id)"
                          >
                            <v-icon>cancel</v-icon>
                          </v-btn>
                        </td>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-flex>
              <!--save & reset-->
              <v-flex sm12>
                <div class="text-xs-center">
                  <v-btn color="error" @click="clear">
                    Reset
                    <v-icon right>clear</v-icon>
                  </v-btn>
                  <v-btn
                    color="primary"
                    :loading="saveLoading"
                    :disabled="!valid || saveLoading"
                    @click="onSave"
                  >
                    Save
                    <v-icon right>save</v-icon>
                  </v-btn>
                </div>
              </v-flex>
            </v-layout>
          </v-card>
        </v-flex>
      </v-layout>
    </v-form>

    <!-- error message dialog -->
    <v-dialog
      v-model="errorMessageBox"
      max-width="500px"
      @keydown.enter="closeErrMsgBox"
      @keydown.esc="closeErrMsgBox"
    >
      <v-card>
        <v-card-text>
          <v-flex>
            <v-alert type="warning" :value="true">
              <h3>{{ responseError }}</h3>
            </v-alert>
          </v-flex>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" flat @click="closeErrMsgBox"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end error message dialog -->
  </v-container>
</template>

<script>
import { machineService } from "../../services/machine-service";
import { unitService } from "../../services/unit-service";
import { machineTypeService } from "../../services/machine-type-service";
import { countryService } from "../../services/country-service";
import { shopService } from "../../services/shop-service";
import { departmentService } from "../../services/department-service";
import { maintenanceFrequencyService } from "../../services/maintenance-frequency-service";
import { maintenanceTypeService } from "../../services/maintenance-type-service";
import { branchService } from "../../services/branch-service";
import {
  Machine,
  MachineDetail,
  MachineChecklistDetail,
} from "../../models/model";
import { commonService } from "../../services/common-service";
import VSwitch from "vuetify/lib/components/VSwitch/VSwitch";
import { checkListFrequencyService } from "../../services/check-list-frequency-service";
import { MachineFormRules } from "../../helpers/validation-rules";

export default {
  name: "MachineDetail",
  components: { VSwitch },
  data: () => ({
    user: JSON.parse(localStorage.getItem("user")),
    valid: true,
    saveLoading: false,
    errorMessageBox: false,
    breadcrumbs: [
      {
        text: "Machine",
        disabled: false,
        to: "machines",
      },
      {
        text: "Machine Detail",
        disabled: true,
      },
    ],
    machineChecklistHeaders: [
      { text: "Location(Device Name)", align: "left", sortable: false },
      { text: "Work Description", sortable: false },
      { text: "Maintenance Frequency", sortable: false },
      { text: "Maintenance Remark", sortable: false },
      { text: "Check List Frequency", sortable: false },
      { text: "Check List Remark", sortable: false },
      { text: "Action", sortable: false },
    ],
    maintenanceRecordHeaders: [
      { text: "Failure Mode", align: "left", sortable: false },
      { text: "Date", sortable: false },
      { text: "Failure Item", sortable: false },
      { text: "Corrective Action", sortable: false },
      { text: "Maintenance Amount", sortable: false },
      { text: "Frequency", sortable: false },
      { text: "Action", sortable: false },
    ],
    statusList: [
      { label: "SERVICEABLE", value: "SERVICEABLE" },
      { label: "ACTIVE", value: "ACTIVE" },
      { label: "INACTIVE", value: "INACTIVE" },
    ],
    machine: new Machine(),
    oldMachine: new Machine(),
    machineDetail: new MachineDetail(),
    machineChecklistDetail: new MachineChecklistDetail(),
    rules: new MachineFormRules(),
    createdDate: "",
    updatedDate: "",
    responseError: "",
    failureItemSearch: "",
    maintenanceFrequencyList: [],
    checklistFrequencyList: [],
    shopList: [],
    departmentList: [],
    branchList: [],
    unitList: [],
    machineList: [],
    maintenanceTypeList: [],
    machineTypeList: [],
    countryList: [],
    date: "",
    unitListByMachine: [],
    totalMaintenanceAmount: 0,
    totalFrequency: 0,
  }),
  created() {
    this.getShops();
    this.getActiveDepartments();
    this.getActiveBranches();
    this.getActitveUnits();
    this.getActiveMachineTypes();
    this.getActiveCountry();
    this.getActiveMaintenanceTypes();
    this.getActiveMaintenanceFrequencies();
    this.getActiveCheckListFrequencies();

    this.machine.branchId = this.checkUserBranch();
    this.machine.shopId = this.checkUserShop();

    const id = this.$route.params.id;
    if (id != "new") {
      this.getMachineDetail(id);
    }
  },
  computed: {
    machineDetailList() {
      return this.machine.machineDetailList.filter((d) => {
        if (this.failureItemSearch)
          return (
            d.failureItem
              .toLowerCase()
              .indexOf(this.failureItemSearch.toLowerCase()) > -1
          );
        else return true;
      });
    },
  },
  methods: {
    calculateFilterTotalAmount() {
      this.totalFrequency = 0;
      this.totalMaintenanceAmount = 0;
      if (this.failureItemSearch) {
        this.machine.machineDetailList.forEach((v) => {
          if (
            v.failureItem
              .toLowerCase()
              .indexOf(this.failureItemSearch.toLowerCase()) > -1
          ) {
            this.totalFrequency += Number(v.frequency);
            this.totalMaintenanceAmount += Number(v.maintenanceAmount);
          }
        });
      } else {
        this.machine.machineDetailList.forEach((v) => {
          this.totalFrequency += Number(v.frequency);
          this.totalMaintenanceAmount += Number(v.maintenanceAmount);
        });
      }
    },
    checkUserShop() {
      if (
        this.user.role.roleName == "BRANCH_ADMIN" ||
        this.user.role.roleName == "ASSET_MANAGER"
      ) {
        return this.user.shopId;
      } else {
        return "";
      }
    },
    checkUserBranch() {
      // if (this.user.role.roleName == "ASSET_MANAGER") {
      //   return this.user.branchId;
      // } else {
      return "";
      // }
    },
    generateId(detailType) {
      let duplicateFlag = true;
      let id;

      // generate random id until it's unique
      while (duplicateFlag) {
        id = Math.floor(Math.random() * 9999 + 1);

        // find if id is duplicated
        let duplicateDetail;
        if (detailType == "Maintenance") {
          duplicateDetail = this.machine.machineDetailList.find(
            (d) => d.id == id
          );
        } else if (detailType == "Machine") {
          duplicateDetail = this.machine.machineChecklistDetailList.find(
            (d) => d.id == id
          );
        }
        if (!duplicateDetail) duplicateFlag = false;
      }
      return id;
    },
    addMachineDetail() {
      // deep copy
      let machineDetail = JSON.parse(JSON.stringify(this.machineDetail));
      // if no id, add temporary id for key
      if (!machineDetail.id) {
        machineDetail.isNew = true;
        machineDetail.id = this.generateId("Maintenance");
      }
      this.machine.machineDetailList.unshift(machineDetail);
    },
    addMachineChecklistDetail() {
      // deep copy
      let machineChecklistDetail = JSON.parse(
        JSON.stringify(this.machineChecklistDetail)
      );
      // if no id, add temporary id for key
      if (!machineChecklistDetail.id) {
        machineChecklistDetail.isNew = true;
        machineChecklistDetail.id = this.generateId("Machine");
      }
      this.machine.machineChecklistDetailList.unshift(machineChecklistDetail);
    },
    removeMachineDetail(machineDetailId) {
      const index = this.machine.machineDetailList
        .map((d) => d.id)
        .indexOf(machineDetailId);

      if (!this.machine.machineDetailList[index].isNew) {
        if (!this.machine.deleteMachineDetailIds) {
          this.machine.deleteMachineDetailIds = [];
        }
        this.machine.deleteMachineDetailIds.push(machineDetailId);
      }
      this.$delete(this.machine.machineDetailList, index);
      this.calculateFilterTotalAmount();
      this.calculateCurrentAmount();
    },
    removeMachineChecklistDetail(machineChecklistDetailId) {
      const index = this.machine.machineChecklistDetailList
        .map((d) => d.id)
        .indexOf(machineChecklistDetailId);

      if (!this.machine.machineChecklistDetailList[index].isNew) {
        if (!this.machine.deleteMachineChecklistDetailIds) {
          this.machine.deleteMachineChecklistDetailIds = [];
        }
        this.machine.deleteMachineChecklistDetailIds.push(
          machineChecklistDetailId
        );
      }
      this.$delete(this.machine.machineChecklistDetailList, index);
    },
     calculateCurrentAmount() {
      this.machine.currentAmount = 0;
      this.machine.currentAmount = Number(this.machine.purchaseOrderAmount);
      this.machine.machineDetailList.forEach(
        (v) => (this.machine.currentAmount += Number(v.maintenanceAmount))
      );
      this.machine.currentAmount = parseFloat(
        this.machine.currentAmount.toFixed(2)
      );
    },
    getActiveBranches() {
      branchService
        .getBranchByStatus("ACTIVE")
        .then((res) => {
          this.branchList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getShops() {
      shopService
        .getAllShop()
        .then((res) => {
          this.shopList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getActiveDepartments() {
      departmentService
        .getDepartmentsByStatus("ACTIVE")
        .then((res) => {
          this.departmentList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getActitveUnits() {
      unitService
        .getUnitsByStatus("ACTIVE")
        .then((res) => {
          this.unitList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getActiveMachineTypes() {
      machineTypeService
        .getMachineTypeByStatus("ACTIVE")
        .then((res) => {
          this.machineTypeList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getActiveCountry() {
      countryService
        .getCountryByStatus("ACTIVE")
        .then((res) => {
          this.countryList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getActiveMaintenanceTypes() {
      maintenanceTypeService
        .getMaintenanceTypesByStatus("ACTIVE")
        .then((res) => {
          this.maintenanceTypeList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getActiveMaintenanceFrequencies() {
      maintenanceFrequencyService
        .getMaintenanceFrequencyByStatus("ACTIVE")
        .then((res) => {
          this.maintenanceFrequencyList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getActiveCheckListFrequencies() {
      checkListFrequencyService
        .getCheckListFrequencyByStatus("ACTIVE")
        .then((res) => {
          this.checklistFrequencyList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getMachineDetail(id) {
      machineService
        .getMachineDetail(id)
        .then((res) => {
          this.machine = res.data;
          //convert milliseconds to date
          this.createdDate = commonService.millisecondToDate(
            this.machine.createdDateInMilliSeconds,
            "YYYY-MM-DD"
          );
          this.updatedDate = commonService.millisecondToDate(
            this.machine.updatedDateInMilliSeconds,
            "YYYY-MM-DD"
          );
          this.machine.machineDetailList.forEach((detail) => {
            if (detail.dateInMilliSeconds) {
              detail.date = commonService.millisecondToDate(
                detail.dateInMilliSeconds,
                "YYYY-MM-DD"
              );
            }
          });
          //if inactive, add to list for show in UI
          if (res.data.department) {
            this.departmentList.push(res.data.department);
          }
          if (res.data.branch) {
            this.branchList.push(res.data.branch);
          }
          if (res.data.unit) {
            this.unitList.push(res.data.unit);
          }
          if (res.data.maintenanceType) {
            this.maintenanceTypeList.push(res.data.maintenanceType);
          }
          if (res.data.country) {
            this.countryList.push(res.data.country);
          }
          if (res.data.machineType) {
            this.machineTypeList.push(res.data.machineType);
          }
          if (res.data.maintenanceFrequency) {
            this.maintenanceFrequencyList.push(res.data.maintenanceFrequency);
          }

          this.calculateFilterTotalAmount();
          this.calculateCurrentAmount();
          // deep copy
          this.oldMachine = JSON.parse(JSON.stringify(res.data));
          this.statusFlag = this.machine.status == "ACTIVE" ? true : false;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    onSave() {
      if (this.$refs.form.validate()) {
        if (!this.saveLoading) {
          this.saveLoading = true;
          let machineObj = this.machine;
          machineObj.machineDetailList.forEach((detail) => {
            //clear id for new machine detail
            if (detail.isNew) detail.id = null;
            //convert to milliseconds
            if (detail.date) {
              detail.dateInMilliSeconds = commonService.convertToMilliseconds(
                detail.date
              );
            } else {
              detail.dateInMilliSeconds = "";
            }
          });
          machineObj.machineChecklistDetailList.forEach((detail) => {
            //clear id for new machine checklist
            if (detail.isNew) detail.id = null;
          });
          machineService
            .saveMachine(machineObj)
            .then((res) => {
              console.log(res);
              this.goBack();
            })
            .catch((err) => {
              console.log(err);
              if (err.response.data) {
                this.responseError = err.response.data.message;
                this.errorMessageBox = true;
              }
              this.saveLoading = false;
            });
        }
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    clear() {
      this.$refs.form.resetValidation();
      this.responseError = "";
      if (!this.machine.id) {
        this.machine = new Machine();
        this.statusFlag = true;
      } else {
        this.machine = this.oldMachine;
        this.statusFlag = this.machine.status == "ACTIVE" ? true : false;
      }
    },
    closeErrMsgBox() {
      this.errorMessageBox = false;
      this.responseError = "";
    },
  },
};
</script>
